<template>
  <v-container fluid class="mapa-page pa-6">
    <!-- Cabecalho -->
    <v-row align="center" class="mb-2" no-gutters>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-icon color="primary" large class="mr-3">mdi-map-marker-radius-outline</v-icon>
          <div>
            <h2 class="titulo-pagina">Mapa de Calor de Cadastros</h2>
            <div class="caption grey--text">
              Concentração geográfica dos cadastros da equipe — áreas quentes e vazios de cobertura
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-md-end align-center mt-3 mt-md-0">
        <v-select
          v-if="ehAdmin"
          v-model="select_equipe"
          :items="candidatos"
          item-text="nome_completo"
          item-value="idcandidato"
          label="Equipe"
          dense
          hide-details
          outlined
          class="styled-select mr-3"
          style="max-width: 260px"
          @change="carregarDados"
        ></v-select>
        <v-btn color="primary" outlined :loading="loading" @click="carregarDados">
          <v-icon left>mdi-refresh</v-icon>Atualizar
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="semCandidato" type="info" text border="left" class="mt-4">
      Selecione uma equipe para visualizar o mapa de calor.
    </v-alert>

    <template v-else>
      <!-- KPIs -->
      <v-row class="mt-1">
        <v-col v-for="kpi in kpis" :key="kpi.titulo" cols="6" sm="4" md="2">
          <v-card outlined class="kpi-card pa-3">
            <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
            <template v-else>
              <div class="caption grey--text text-uppercase d-flex align-center">
                {{ kpi.titulo }}
                <v-icon
                  v-if="kpi.info"
                  x-small
                  class="ml-1"
                  color="grey"
                  @click="dialogInfo = true"
                >mdi-information-outline</v-icon>
              </div>
              <div class="kpi-valor" :class="kpi.cor">{{ kpi.valor }}</div>
              <div class="caption grey--text">{{ kpi.legenda }}</div>
            </template>
          </v-card>
        </v-col>
      </v-row>


      <!-- Mapa -->
      <v-card outlined class="table-card mt-4">
        <v-tabs v-model="tab" background-color="transparent" color="primary">
          <v-tab>Bairros de Manaus</v-tab>
          <v-tab>Municípios do AM</v-tab>
          <v-tab>Zonas de Manaus</v-tab>
        </v-tabs>
        <v-divider></v-divider>

        <div class="pa-4">
          <div class="d-flex flex-wrap align-center mb-3">
            <v-btn-toggle v-model="metrica" mandatory dense group color="primary">
              <v-tooltip v-for="m in metricas" :key="m.valor" bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn :value="m.valor" small v-bind="attrs" v-on="on">
                    {{ m.rotulo }}
                  </v-btn>
                </template>
                <span>{{ m.descricao }}</span>
              </v-tooltip>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-switch
              v-if="granularidade !== 'bairro' && recuperaveisPorZona > 0"
              v-model="posicionarPorZona"
              dense
              hide-details
              class="mt-0 mr-4"
            >
              <template v-slot:label>
                <span class="body-2">Posicionar pela zona</span>
                <v-tooltip bottom max-width="320">
                  <template v-slot:activator="{ on }">
                    <v-icon x-small class="ml-1" color="grey" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>
                    Cadastros com seção inválida são posicionados pela zona eleitoral, que é sempre
                    válida — apenas quando a zona corresponde a um único
                    {{ granularidade === "municipio" ? "município" : "região" }}.
                    Recupera {{ formatNumber(recuperaveisPorZona) }} cadastros.
                  </span>
                </v-tooltip>
              </template>
            </v-switch>
            <v-switch
              v-if="granularidade === 'municipio'"
              v-model="excluirManaus"
              label="Escala sem Manaus"
              dense
              hide-details
              class="mt-0 mr-4"
            ></v-switch>
            <v-chip
              v-if="areasSemPoligono.length"
              small
              color="warning"
              text-color="white"
              class="mr-2"
              @click="dialogAuditoria = true"
            >
              <v-icon left small>mdi-alert</v-icon>
              {{ areasSemPoligono.length }} sem contorno
            </v-chip>
          </div>

          <div class="caption grey--text text--darken-1 mb-3 descricao-metrica">
            <v-icon x-small class="mr-1" color="primary">mdi-information-outline</v-icon>
            <strong>{{ metricaAtual.rotulo }}:</strong> {{ metricaAtual.descricao }}
          </div>

          <v-alert v-if="tilesIndisponiveis" type="warning" text dense border="left">
            Mapa base indisponível (sem acesso à internet). As áreas continuam sendo exibidas.
          </v-alert>

          <!-- Legenda acima do mapa: sobreposta ao mapa ela cobria o tooltip
               (os panes do Leaflet ficam confinados no z-index 400 do map-pane,
               entao qualquer overlay acima disso vence o tooltip), e abaixo do
               mapa saia da tela agora que o mapa e mais alto. -->
          <div v-if="legenda.length" class="d-flex flex-wrap align-center legenda-barra mb-2">
            <span class="caption grey--text mr-2">{{ metricaAtual.rotulo }}:</span>
            <div v-for="(faixa, i) in legenda" :key="i" class="legenda-item">
              <span class="legenda-cor" :style="{ backgroundColor: faixa.cor }"></span>
              <span class="caption">{{ faixa.rotulo }}</span>
            </div>
            <div class="legenda-item">
              <span class="legenda-cor" :style="{ backgroundColor: corSemDado }"></span>
              <span class="caption">sem dado</span>
            </div>
          </div>

          <div class="mapa-wrapper">
            <div ref="mapa" class="mapa-canvas"></div>

            <div v-if="loading" class="mapa-overlay">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
          </div>

          <!-- Conferencia -->
          <div v-if="ehMetricaVoto" class="caption grey--text mt-3 rodape-conferencia">
            {{ formatNumber(somaVotos) }} de {{ formatNumber(totais.total_votos) }} votos no mapa
          </div>
          <div v-else class="caption grey--text mt-3 rodape-conferencia">
            {{ formatNumber(somaAreas) }} de {{ formatNumber(totais.total_cadastrados) }} cadastros
            no mapa
            <template v-if="posicionarPorZona && posicionadosNoNivel > 0">
              · {{ formatNumber(posicionadosNoNivel) }} posicionados pela zona
            </template>
            <v-icon x-small class="ml-1" color="grey" @click="dialogInfo = true"
              >mdi-information-outline</v-icon
            >
          </div>
        </div>
      </v-card>

      <!-- Tabela: fonte de verdade e fallback quando o mapa nao carrega -->
      <v-card outlined class="table-card mt-4">
        <v-card-title class="pb-0">
          <span class="subtitle-1">Detalhamento por área</span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="busca"
            append-icon="mdi-magnify"
            label="Buscar área"
            single-line
            hide-details
            dense
            outlined
            class="styled-select"
            style="max-width: 260px"
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="linhas"
          :search="busca"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
          class="clickable-rows"
          @click:row="abrirArea"
        >
          <template v-slot:item.cadastrados="{ item }">
            {{ formatNumber(item.cadastrados) }}
          </template>
          <template v-slot:item.eleitorado="{ item }">
            {{ formatNumber(item.eleitorado) }}
          </template>
          <template v-slot:item.penetracao="{ item }">
            <span v-if="item.penetracao === null" class="grey--text">—</span>
            <span v-else>{{ item.penetracao.toFixed(2) }}%</span>
          </template>
          <template v-slot:item.oportunidade="{ item }">
            {{ formatNumber(item.oportunidade) }}
          </template>
          <template v-slot:item.votos="{ item }">
            {{ formatNumber(item.votos) }}
          </template>
          <template v-slot:item.participacao="{ item }">
            <span v-if="item.participacao === null" class="grey--text">—</span>
            <span v-else>{{ item.participacao.toFixed(2) }}%</span>
          </template>
          <template v-slot:item.aproveitamento="{ item }">
            <span v-if="item.aproveitamento === null" class="grey--text">—</span>
            <v-chip v-else x-small dark :color="corAproveitamento(item.aproveitamento)">
              {{ item.aproveitamento.toFixed(1) }}%
            </v-chip>
          </template>
          <template v-slot:item.mapeada="{ item }">
            <v-icon v-if="temPoligono(item)" small color="success">mdi-check-circle-outline</v-icon>
            <v-tooltip v-else bottom>
              <template v-slot:activator="{ on }">
                <v-icon small color="warning" v-on="on">mdi-map-marker-off-outline</v-icon>
              </template>
              <span>Sem contorno disponível — aparece só na tabela</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Drill-down da area -->
    <v-dialog v-model="dialogArea" max-width="520">
      <v-card class="dialog-card">
        <v-card-title class="subtitle-1">
          {{ areaSelecionada.nome }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogArea = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="6">
              <div class="caption grey--text text-uppercase">Cadastrados</div>
              <div class="kpi-valor">{{ formatNumber(areaSelecionada.cadastrados) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text text-uppercase">Eleitorado</div>
              <div class="kpi-valor">{{ formatNumber(areaSelecionada.eleitorado) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text text-uppercase">Penetração</div>
              <div class="kpi-valor">
                <span v-if="areaSelecionada.penetracao === null">—</span>
                <span v-else>{{ areaSelecionada.penetracao.toFixed(2) }}%</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text text-uppercase">Assessores atuantes</div>
              <div class="kpi-valor">{{ formatNumber(areaSelecionada.assessores) }}</div>
            </v-col>
            <template v-if="totais.tem_apuracao">
              <v-col cols="4">
                <div class="caption grey--text text-uppercase">Votos</div>
                <div class="kpi-valor">{{ formatNumber(areaSelecionada.votos) }}</div>
              </v-col>
              <v-col cols="4">
                <div class="caption grey--text text-uppercase">Participação</div>
                <div class="kpi-valor">
                  <span v-if="areaSelecionada.participacao === null">—</span>
                  <span v-else>{{ areaSelecionada.participacao.toFixed(2) }}%</span>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="caption grey--text text-uppercase">Aproveitamento</div>
                <div class="kpi-valor">
                  <span v-if="areaSelecionada.aproveitamento === null">—</span>
                  <span v-else>{{ areaSelecionada.aproveitamento.toFixed(1) }}%</span>
                </div>
              </v-col>
            </template>
            <v-col cols="12">
              <div class="caption grey--text text-uppercase">Oportunidade</div>
              <div class="kpi-valor">{{ formatNumber(areaSelecionada.oportunidade) }}</div>
              <div class="caption grey--text">eleitores ainda não cadastrados na área</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <span class="caption grey--text">
            {{ formatNumber(areaSelecionada.assessores) }} assessores atuaram nesta área
          </span>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="!areaSelecionada.assessores"
            @click="exportarAssessores"
          >
            <v-icon left small>mdi-file-excel</v-icon>Exportar assessores
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Explicacao dos cadastros sem area -->
    <v-dialog v-model="dialogInfo" max-width="560">
      <v-card class="dialog-card">
        <v-card-title class="subtitle-1">
          Cadastros sem área no mapa
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogInfo = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 body-2">
          <p>
            Uma pessoa não tem endereço cadastrado: sua localização vem do par
            <strong>zona + seção</strong> eleitoral. Quando essa combinação não consta na base de
            seções, não há bairro nem município — e ela não pode ser pintada em nenhuma área.
          </p>
          <p>
            São <strong>{{ formatNumber(totais.nao_localizados) }} cadastros</strong>
            ({{ totais.pct_nao_localizados }}%). Eles continuam contados no total da equipe.
          </p>
          <p class="mb-0">
            A <strong>zona</strong> informada é válida em todos os casos. Por isso o mapa oferece
            posicionar esses cadastros pela zona, nas visões de município e de região — mas só
            quando a zona corresponde a um único deles. Por bairro isso não é possível: uma zona
            cobre vários bairros.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Auditoria do casamento com os contornos -->
    <v-dialog v-model="dialogAuditoria" max-width="620">
      <v-card class="dialog-card">
        <v-card-title class="subtitle-1">
          Áreas sem contorno no mapa
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogAuditoria = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <p class="caption grey--text">
            Estas áreas têm cadastros mas não possuem contorno geográfico disponível.
            Os números continuam válidos na tabela e nos totais — apenas não são pintados no mapa.
          </p>
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">Área</th>
                <th class="text-right">Cadastrados</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in areasSemPoligono" :key="a.chave">
                <td>{{ a.nome }}</td>
                <td class="text-right">{{ formatNumber(a.cadastrados) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AclAPI from "./../../api/acl";
import MapaCalorAPI from "./../../api/mapaCalor";
import { DESCRICOES } from "./descricoesMetricas";
import {
  URL_BAIRROS,
  URL_MUNICIPIOS,
  carregarGeoJson,
  indexarAreas,
  areaDoFeature,
  calcularQuebras,
  corPorQuebras,
  COR_SEM_DADO,
} from "./mapaCalorGeo";

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const CENTRO_MANAUS = [-3.08, -60.0];
const CENTRO_AM = [-4.5, -64.5];
// A zona rural de Manaus e desproporcional a mancha urbana: fica pintada, mas
// fora do calculo do enquadramento inicial.
const CHAVES_SEM_ENQUADRAMENTO = ["ZONA RURAL"];

/** Metricas do boletim de urna (so aparecem com apuracao importada). */
const METRICAS_VOTO = [
  { valor: "votos", rotulo: "Votos", descricao: DESCRICOES.votos },
  { valor: "participacao", rotulo: "Participação", descricao: DESCRICOES.participacao },
  { valor: "aproveitamento", rotulo: "Aproveitamento", descricao: DESCRICOES.aproveitamento },
];

export default {
  name: "MapaCalorCadastros",

  data() {
    return {
      loading: false,
      ehAdmin: false,
      select_equipe: "null",
      candidatos: [],
      tab: 0,
      metrica: "cadastrados",
      excluirManaus: false,
      posicionarPorZona: false,
      dialogInfo: false,
      busca: "",
      areas: { bairro: [], municipio: [], regiao: [] },
      totais: {
        total_cadastrados: 0,
        nao_localizados: 0,
        pct_nao_localizados: 0,
        pares_desconhecidos: 0,
        assessores: 0,
        posicionados_por_zona: { bairro: 0, municipio: 0, regiao: 0 },
        tem_apuracao: false,
        total_votos: 0,
        votos_localizados: 0,
      },
      geo: { bairros: null, municipios: null },
      chavesComPoligono: [],
      tilesIndisponiveis: false,
      dialogArea: false,
      dialogAuditoria: false,
      areaSelecionada: {
        nome: "",
        cadastrados: 0,
        eleitorado: 0,
        penetracao: null,
        assessores: 0,
        oportunidade: 0,
        votos: 0,
        pontuacao: 0,
        participacao: null,
        aproveitamento: null,
      },
      corSemDado: COR_SEM_DADO,
      metricasBase: [
        { valor: "cadastrados", rotulo: "Volume", descricao: DESCRICOES.cadastros },
        { valor: "penetracao", rotulo: "Penetração", descricao: DESCRICOES.penetracao },
        { valor: "oportunidade", rotulo: "Oportunidade", descricao: DESCRICOES.oportunidade },
        { valor: "assessores", rotulo: "Assessores", descricao: DESCRICOES.assessoresAtuantes },
      ],
    };
  },

  computed: {
    semCandidato() {
      return !this.select_equipe || this.select_equipe === "null";
    },

    granularidade() {
      return ["bairro", "municipio", "regiao"][this.tab];
    },

    linhas() {
      return this.areas[this.granularidade] || [];
    },

    /** As metricas de voto so existem com o boletim de urna importado. */
    metricas() {
      if (!this.totais.tem_apuracao) return this.metricasBase;
      return this.metricasBase.concat(METRICAS_VOTO);
    },

    ehMetricaVoto() {
      return METRICAS_VOTO.some((m) => m.valor === this.metrica);
    },

    somaVotos() {
      return this.linhas.reduce((s, l) => s + (l.votos || 0), 0);
    },

    metricaAtual() {
      const m = this.metricas.filter((x) => x.valor === this.metrica);
      return m.length ? m[0] : this.metricas[0];
    },

    indice() {
      return indexarAreas(this.linhas);
    },

    /**
     * As zonas de Manaus nao tem contorno proprio: o mapa usa os poligonos de
     * bairro, e cada um precisa saber a que regiao pertence para ser pintado.
     */
    regiaoPorBairro() {
      const mapa = {};
      (this.areas.bairro || []).forEach((b) => {
        if (b.id_zona_geografica !== null && b.id_zona_geografica !== undefined) {
          mapa[String(b.chave)] = String(b.id_zona_geografica);
        }
      });
      return mapa;
    },

    /** Valores da metrica corrente, base da escala de cores. */
    valoresEscala() {
      let linhas = this.linhas;
      if (this.granularidade === "municipio" && this.excluirManaus) {
        linhas = linhas.filter((l) => String(l.chave) !== "38");
      }
      return linhas
        .map((l) => l[this.metrica])
        .filter((v) => v !== null && v !== undefined);
    },

    quebras() {
      return calcularQuebras(this.valoresEscala, 5);
    },

    legenda() {
      if (!this.quebras.length) return [];
      const faixas = [];
      let anterior = 0;
      this.quebras.forEach((q) => {
        faixas.push({
          cor: corPorQuebras(anterior, this.quebras),
          rotulo: this.formatFaixa(anterior) + "–" + this.formatFaixa(q),
        });
        anterior = q;
      });
      faixas.push({
        cor: corPorQuebras(anterior + 1, this.quebras),
        rotulo: "> " + this.formatFaixa(anterior),
      });
      return faixas;
    },

    somaAreas() {
      return this.linhas.reduce((s, l) => s + l.cadastrados, 0);
    },

    /** Quantos cadastros a zona conseguiria posicionar na granularidade atual. */
    recuperaveisPorZona() {
      const m = this.totais.posicionados_por_zona;
      return m && m[this.granularidade] ? m[this.granularidade] : 0;
    },

    /** Quantos estao de fato posicionados pela zona agora (0 se o toggle esta off). */
    posicionadosNoNivel() {
      return this.posicionarPorZona ? this.recuperaveisPorZona : 0;
    },

    /** Nao localizados restantes, ja descontando o que a zona posicionou. */
    naoLocalizadosEfetivo() {
      return Math.max(this.totais.nao_localizados - this.posicionadosNoNivel, 0);
    },

    areasSemPoligono() {
      return this.linhas.filter((l) => l.cadastrados > 0 && !this.temPoligono(l));
    },

    headers() {
      const rotulo =
        this.granularidade === "bairro"
          ? "Bairro"
          : this.granularidade === "municipio"
          ? "Município"
          : "Zona";
      const base = [
        { text: rotulo, value: "nome", align: "start" },
        { text: "Cadastrados", value: "cadastrados", align: "end", width: 130 },
        { text: "Eleitorado", value: "eleitorado", align: "end", width: 130 },
        { text: "Penetração", value: "penetracao", align: "end", width: 120 },
        { text: "Oportunidade", value: "oportunidade", align: "end", width: 130 },
        { text: "Assessores", value: "assessores", align: "end", width: 120 },
        { text: "Mapa", value: "mapeada", align: "center", width: 80, sortable: false },
      ];
      if (!this.totais.tem_apuracao) return base;
      // Com o boletim, votos e aproveitamento entram logo depois da penetracao.
      const i = base.findIndex((h) => h.value === "penetracao");
      return [
        ...base.slice(0, i + 1),
        { text: "Votos", value: "votos", align: "end", width: 100 },
        { text: "Participação", value: "participacao", align: "end", width: 120 },
        { text: "Aproveitamento", value: "aproveitamento", align: "end", width: 130 },
        ...base.slice(i + 1),
      ];
    },

    kpis() {
      if (this.ehMetricaVoto) return this.kpisVoto;
      const t = this.totais;
      const areasComCadastro = this.linhas.filter((l) => l.cadastrados > 0).length;
      const eleitorado = this.linhas.reduce((s, l) => s + l.eleitorado, 0);
      const penetracao = eleitorado > 0 ? (this.somaAreas / eleitorado) * 100 : 0;
      return [
        {
          titulo: "Cadastrados",
          valor: this.formatNumber(t.total_cadastrados),
          legenda: "total da equipe",
          cor: "",
        },
        {
          titulo: "No mapa",
          valor: this.formatNumber(this.somaAreas),
          legenda: this.rotuloNivel,
          cor: "",
        },
        {
          titulo: "Eleitorado",
          valor: this.formatNumber(eleitorado),
          legenda: this.rotuloNivel,
          cor: "",
        },
        {
          titulo: "Penetração",
          valor: penetracao.toFixed(2) + "%",
          legenda: "cadastrados / eleitorado",
          cor: "",
        },
        {
          titulo: "Áreas cobertas",
          valor: areasComCadastro + " / " + this.linhas.length,
          legenda: "com ao menos 1 cadastro",
          cor: "",
        },
        {
          titulo: "Sem área",
          valor: this.formatNumber(this.naoLocalizadosEfetivo),
          legenda:
            t.total_cadastrados > 0
              ? (
                  (this.naoLocalizadosEfetivo / t.total_cadastrados) *
                  100
                ).toFixed(2) + "% do total"
              : "0% do total",
          cor: "",
          info: true,
        },
      ];
    },

    /** KPIs quando a metrica ativa e de voto (boletim de urna). */
    kpisVoto() {
      const t = this.totais;
      const eleitorado = this.linhas.reduce((s, l) => s + l.eleitorado, 0);
      const pontuacao = this.linhas.reduce((s, l) => s + (l.pontuacao || 0), 0);
      const areasComVoto = this.linhas.filter((l) => l.votos > 0).length;
      return [
        {
          titulo: "Votos",
          valor: this.formatNumber(t.total_votos),
          legenda: "total do candidato",
          cor: "",
        },
        {
          titulo: "No mapa",
          valor: this.formatNumber(this.somaVotos),
          legenda: this.rotuloNivel,
          cor: "",
        },
        {
          titulo: "Eleitorado",
          valor: this.formatNumber(eleitorado),
          legenda: this.rotuloNivel,
          cor: "",
        },
        {
          titulo: "Participação",
          valor: (eleitorado > 0 ? (this.somaVotos / eleitorado) * 100 : 0).toFixed(2) + "%",
          legenda: "votos / eleitorado",
          cor: "",
        },
        {
          titulo: "Aproveitamento",
          valor: (this.somaAreas > 0 ? (pontuacao / this.somaAreas) * 100 : 0).toFixed(1) + "%",
          legenda: "pontuação / cadastrados",
          cor: "",
        },
        {
          titulo: "Áreas com voto",
          valor: areasComVoto + " / " + this.linhas.length,
          legenda: "com ao menos 1 voto",
          cor: "",
        },
      ];
    },

    rotuloNivel() {
      if (this.granularidade === "municipio") return "todo o Amazonas";
      return "somente Manaus";
    },
  },

  watch: {
    tab() {
      this.busca = "";
      this.renderCamada();
      this.$nextTick(this.ajustarAlturaMapa);
    },
    metrica() {
      this.repintar();
    },
    excluirManaus() {
      this.repintar();
    },
    posicionarPorZona() {
      this.carregarDados();
    },
  },

  created() {
    // Objetos do Leaflet ficam fora de data(): o Vue 2 instrumenta tudo que
    // esta em data com defineProperty, e observar o grafo de um L.Map
    // (containers DOM, panes, camadas, referencias circulares) degrada muito a
    // performance e provoca bugs de renderizacao.
    this.mapa = null;
    this.camada = null;
    this.tileLayer = null;
    this.errosTile = 0;
    this.ALTURA_MIN_MAPA = 420;
  },

  mounted() {
    this.ehAdmin = parseInt(localStorage.getItem("controle"), 10) <= 2;
    const equipe = localStorage.getItem("equipe");
    // localStorage sempre devolve string; o item-value do v-select e numerico.
    this.select_equipe = equipe && equipe !== "null" ? parseInt(equipe, 10) : "null";

    if (this.ehAdmin) {
      this.buscarCandidatos();
    } else if (!this.semCandidato) {
      this.carregarDados();
    }

    this.$nextTick(this.ajustarAlturaMapa);
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.destruirMapa();
  },

  methods: {
    formatNumber(valor) {
      const n = Number(valor);
      return isNaN(n) ? "0" : n.toLocaleString("pt-BR");
    },

    /** Mesma escala de cor do Boletim de Urna e da Cobertura (valor em %). */
    corAproveitamento(pct) {
      if (pct >= 50) return "success";
      if (pct >= 30) return "light-green darken-1";
      if (pct >= 15) return "amber darken-2";
      if (pct > 0) return "orange darken-3";
      return "grey";
    },

    formatFaixa(valor) {
      if (this.metrica === "penetracao" || this.metrica === "participacao") {
        return Number(valor).toFixed(2) + "%";
      }
      if (this.metrica === "aproveitamento") return Number(valor).toFixed(1) + "%";
      return this.formatNumber(Math.round(valor));
    },

    /**
     * Area correspondente a um poligono. No nivel de zonas o poligono e de
     * bairro, entao o casamento passa pela regiao do bairro.
     */
    resolverArea(feature) {
      if (this.granularidade !== "regiao") {
        return areaDoFeature(feature, this.indice);
      }
      const props = feature && feature.properties ? feature.properties : {};
      const idRegiao = this.regiaoPorBairro[String(props.chave)];
      if (!idRegiao) return null;
      return this.indice.porChave[idRegiao] || null;
    },

    /**
     * Altura do mapa = espaco que sobra ate o fim da tela, medido de verdade em
     * vez de estimado no CSS. Sem isso o mapa passa da viewport e o tooltip dos
     * poligonos de baixo aparece fora da area visivel.
     */
    ajustarAlturaMapa() {
      const el = this.$refs.mapa;
      if (!el) return;

      // Posicao do topo do mapa no documento (independe do scroll atual).
      const topoNoDocumento = el.getBoundingClientRect().top + window.pageYOffset;
      const disponivel = window.innerHeight - topoNoDocumento - 16;

      el.style.height = Math.max(this.ALTURA_MIN_MAPA, disponivel) + "px";

      if (this.mapa) {
        this.$nextTick(() => {
          if (this.mapa) this.mapa.invalidateSize();
        });
      }
    },

    onResize() {
      this.ajustarAlturaMapa();
    },

    /** Areas que nao devem ditar o zoom inicial (ver renderCamada). */
    foraDoEnquadramento(feature) {
      const props = feature && feature.properties ? feature.properties : {};
      return CHAVES_SEM_ENQUADRAMENTO.indexOf(String(props.chave)) !== -1;
    },

    temPoligono(item) {
      return this.chavesComPoligono.indexOf(String(item.chave)) !== -1;
    },

    buscarCandidatos() {
      const headers = this.Helpers.getHeaderRequisicao();
      AclAPI.getCandidatos(headers)
        .then((response) => {
          if (response.data.token) localStorage.setItem("token", response.data.token);
          this.candidatos = response.data.candidatos || [];
          // Sem equipe definida: seleciona a primeira (nao existe modo "TODAS").
          if (this.semCandidato && this.candidatos.length) {
            this.select_equipe = this.candidatos[0].idcandidato;
          }
          if (!this.semCandidato) this.carregarDados();
        })
        .catch(() => this.Helpers.logout());
    },

    carregarDados() {
      if (this.semCandidato) return;
      this.loading = true;
      const headers = this.Helpers.getHeaderRequisicao();

      MapaCalorAPI.getMapaCalor(headers, {
        equipe: this.select_equipe,
        posicionar_por_zona: this.posicionarPorZona ? 1 : 0,
      })
        .then((response) => {
          localStorage.setItem("token", response.data.token);
          this.areas = response.data.areas;
          this.totais = response.data.totais;
          if (!this.totais.tem_apuracao && this.ehMetricaVoto) this.metrica = "cadastrados";
          return this.garantirGeo();
        })
        .then(() => {
          this.iniciarMapa();
          this.renderCamada();
        })
        .catch(() => this.Helpers.logout())
        .finally(() => {
          this.loading = false;
          // Os KPIs saem do skeleton e podem mudar de altura: remede o espaco.
          this.$nextTick(this.ajustarAlturaMapa);
        });
    },

    /** Carrega os contornos sob demanda e os mantem em cache na instancia. */
    garantirGeo() {
      const pendentes = [];
      if (!this.geo.bairros) {
        pendentes.push(
          carregarGeoJson(URL_BAIRROS).then((d) => {
            this.geo.bairros = Object.freeze(d);
          })
        );
      }
      if (!this.geo.municipios) {
        pendentes.push(
          carregarGeoJson(URL_MUNICIPIOS).then((d) => {
            this.geo.municipios = Object.freeze(d);
          })
        );
      }
      return Promise.all(pendentes);
    },

    geoDaGranularidade() {
      // As zonas de Manaus nao tem contorno proprio: sao os mesmos poligonos de
      // bairro, pintados pela regiao a que cada bairro pertence.
      return this.granularidade === "municipio" ? this.geo.municipios : this.geo.bairros;
    },

    iniciarMapa() {
      if (this.mapa || !this.$refs.mapa) return;

      this.mapa = L.map(this.$refs.mapa, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: true,
      }).setView(CENTRO_MANAUS, 11);

      this.tileLayer = L.tileLayer(TILE_URL, {
        maxZoom: 18,
        attribution: "© OpenStreetMap",
      });
      this.tileLayer.on("tileerror", this.onErroTile);
      this.tileLayer.addTo(this.mapa);
    },

    /**
     * Sem internet o tile server falha em cada quadro. O coropletico nao depende
     * dele, entao removemos a camada base e seguimos como cartograma.
     */
    onErroTile() {
      this.errosTile += 1;
      if (this.errosTile > 8 && this.tileLayer && this.mapa) {
        this.mapa.removeLayer(this.tileLayer);
        this.tileLayer = null;
        this.tilesIndisponiveis = true;
      }
    },

    renderCamada() {
      if (!this.mapa) return;

      if (this.camada) {
        this.mapa.removeLayer(this.camada);
        this.camada = null;
      }

      const geo = this.geoDaGranularidade();
      if (!geo) return;

      const presentes = {};
      let boundsFoco = null;

      this.camada = L.geoJSON(geo, {
        style: this.estiloFeature,
        onEachFeature: (feature, layer) => {
          const area = this.resolverArea(feature);
          if (area) presentes[String(area.chave)] = true;
          // A zona rural e maior que toda a mancha urbana somada; se entrar no
          // enquadramento, a cidade vira um ponto. Ela continua pintada e
          // acessivel com zoom out — so nao manda no zoom inicial.
          if (!this.foraDoEnquadramento(feature)) {
            const b = layer.getBounds();
            boundsFoco = boundsFoco ? boundsFoco.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
          }
          layer.bindTooltip(this.textoTooltip(feature, area), { sticky: true });
          layer.on({
            click: () => {
              if (area) this.abrirArea(area);
            },
            mouseover: (e) => e.target.setStyle({ weight: 2.5, color: "#0E314A" }),
            mouseout: (e) => this.camada.resetStyle(e.target),
          });
        },
      }).addTo(this.mapa);

      this.chavesComPoligono = Object.keys(presentes);

      const bounds = boundsFoco && boundsFoco.isValid() ? boundsFoco : this.camada.getBounds();
      if (bounds.isValid()) {
        this.mapa.fitBounds(bounds, { padding: [12, 12] });
      } else {
        this.mapa.setView(
          this.granularidade === "municipio" ? CENTRO_AM : CENTRO_MANAUS,
          this.granularidade === "municipio" ? 6 : 11
        );
      }

      // O container fica oculto enquanto a aba nao esta ativa; sem isto o
      // Leaflet mede 0x0 e o mapa aparece cinza.
      this.$nextTick(() => {
        if (this.mapa) this.mapa.invalidateSize();
      });
    },

    repintar() {
      if (this.camada) this.camada.setStyle(this.estiloFeature);
    },

    estiloFeature(feature) {
      const area = this.resolverArea(feature);
      const valor = area ? area[this.metrica] : null;
      // No mapa de zonas os poligonos sao de bairro: divisas quase invisiveis
      // para que cada regiao leia como uma mancha unica.
      const ehRegiao = this.granularidade === "regiao";
      return {
        fillColor: corPorQuebras(valor, this.quebras),
        fillOpacity: ehRegiao ? 0.8 : 0.72,
        weight: ehRegiao ? 0.3 : 0.8,
        color: "#ffffff",
        opacity: ehRegiao ? 0.5 : 1,
      };
    },

    textoTooltip(feature, area) {
      const nome =
        area && area.nome
          ? area.nome
          : feature.properties && feature.properties.nome
          ? feature.properties.nome
          : "—";
      if (!area) return "<strong>" + nome + "</strong><br>sem dado";
      const penetracao =
        area.penetracao === null ? "—" : area.penetracao.toFixed(2) + "%";
      return (
        "<strong>" + nome + "</strong><br>" +
        "Cadastrados: " + this.formatNumber(area.cadastrados) + "<br>" +
        "Eleitorado: " + this.formatNumber(area.eleitorado) + "<br>" +
        "Penetração: " + penetracao + "<br>" +
        "Assessores: " + this.formatNumber(area.assessores) +
        (this.totais.tem_apuracao
          ? "<br>Votos: " + this.formatNumber(area.votos) +
            "<br>Aproveitamento: " +
            (area.aproveitamento === null ? "—" : area.aproveitamento.toFixed(1) + "%")
          : "")
      );
    },

    abrirArea(area) {
      this.areaSelecionada = area;
      this.dialogArea = true;
    },

    /**
     * Excel com os assessores que cadastraram na area. Rota publica (aberta em
     * nova aba, sem Bearer), autorizada pelo id do usuario — mesmo padrao dos
     * demais relatorios. Leva o estado do toggle para a lista bater com o card.
     */
    exportarAssessores() {
      const area = this.areaSelecionada;
      if (!area || !area.assessores) return;

      const url =
        "relatorio_assessores_area?equipe=" +
        this.select_equipe +
        "&nivel=" +
        this.granularidade +
        "&chave=" +
        encodeURIComponent(area.chave) +
        "&posicionar_por_zona=" +
        (this.posicionarPorZona ? 1 : 0) +
        "&id_cd=" +
        localStorage.getItem("id");

      window.open(process.env.VUE_APP_URL_BASE + "/api/" + url, "_blank");
    },

    destruirMapa() {
      if (this.mapa) {
        this.mapa.off();
        this.mapa.remove();
      }
      this.mapa = null;
      this.camada = null;
      this.tileLayer = null;
    },
  },
};
</script>

<style scoped>
.mapa-page {
  background-color: #f5f7fb;
  min-height: 100vh;
}
.titulo-pagina {
  font-size: 1.35rem;
  font-weight: 600;
  color: #0e314a;
}
.kpi-card {
  border-radius: 16px;
}
.kpi-valor {
  font-size: 1.35rem;
  font-weight: 600;
  color: #0e314a;
  line-height: 1.4;
}
.table-card {
  border-radius: 16px;
}
.dialog-card {
  border-radius: 16px;
}
.styled-select >>> .v-input__slot {
  border-radius: 12px;
}
.mapa-wrapper {
  position: relative;
}
.mapa-canvas {
  /* Fallback: a altura real e medida em ajustarAlturaMapa(), para o mapa
     terminar dentro da viewport. Se o mapa passar da tela, o tooltip da parte
     de baixo aparece fora da area visivel. */
  height: 520px;
  min-height: 420px;
  width: 100%;
  border-radius: 12px;
  background-color: #eef1f5;
  /* O mapa PRECISA ser contido num contexto de empilhamento proprio: os panes do
     Leaflet vao de 400 a 700 e os controles chegam a 1000, enquanto os overlays
     do Vuetify usam ~8 (tooltip/menu) e ~202 (dialog) — sem isso o mapa cobre
     modais e tooltips. `z-index: 0` contem sem elevar.
     Corolario: nao sobrepor nenhum elemento ao mapa (a legenda fica ACIMA dele,
     fora do container), senao esse elemento passa por cima do tooltip do mapa. */
  position: relative;
  z-index: 0;
}
.mapa-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  z-index: 1000; /* acima de todos os panes do Leaflet */
}
.descricao-metrica {
  margin-top: -4px;
}
.legenda-barra {
  min-height: 24px;
}
.legenda-item {
  display: flex;
  align-items: center;
  margin-right: 14px;
}
.legenda-cor {
  width: 16px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  margin-right: 5px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.rodape-conferencia {
  border-top: 1px solid #eceff1;
  padding-top: 8px;
}
.clickable-rows >>> tbody tr {
  cursor: pointer;
}
</style>
